import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, Star, TrendingUp } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const BarberDashboard = () => {
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => {
    fetchStats();
    fetchAppointments();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/barber/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch stats');
    }
  };

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/barber/appointments/upcoming');
      setAppointments(response.data);
    } catch (error) {
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteAppointment = async (appointmentId) => {
    try {
      await api.put(`/barber/appointments/${appointmentId}/complete`);
      toast.success('Appointment marked as completed');
      fetchAppointments();
      fetchStats();
    } catch (error) {
      toast.error('Failed to complete appointment');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8 dark:text-white">Barber Dashboard</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Total Appointments</p>
                <p className="text-3xl font-bold dark:text-white">{stats?.totalAppointments || 0}</p>
              </div>
              <Calendar size={32} className="text-blue-600" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Completed</p>
                <p className="text-3xl font-bold dark:text-white">{stats?.completedAppointments || 0}</p>
              </div>
              <TrendingUp size={32} className="text-green-600" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Total Earnings</p>
                <p className="text-3xl font-bold text-blue-600">₹{stats?.totalEarnings || 0}</p>
              </div>
              <DollarSign size={32} className="text-orange-600" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Rating</p>
                <p className="text-3xl font-bold dark:text-white">{stats?.rating || 0}</p>
              </div>
              <Star size={32} className="text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-gray-300 dark:border-gray-700">
          {['upcoming', 'completed', 'availability', 'profile'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-semibold capitalize transition border-b-2 ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-blue-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="card">
          {activeTab === 'upcoming' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 dark:text-white">Upcoming Appointments</h2>
              {loading ? (
                <p>Loading...</p>
              ) : appointments.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">No upcoming appointments</p>
              ) : (
                <div className="space-y-4">
                  {appointments.map((apt) => (
                    <div key={apt._id} className="flex items-center justify-between p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-semibold dark:text-white">{apt.customer?.firstName}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(apt.appointmentDate).toLocaleDateString()} at {apt.startTime}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCompleteAppointment(apt._id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                      >
                        Complete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'completed' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 dark:text-white">Completed Appointments</h2>
              <p className="text-gray-600 dark:text-gray-400">Completed appointments history coming soon...</p>
            </div>
          )}

          {activeTab === 'availability' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 dark:text-white">Set Your Availability</h2>
              <p className="text-gray-600 dark:text-gray-400">Availability management features coming soon...</p>
            </div>
          )}

          {activeTab === 'profile' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 dark:text-white">My Profile</h2>
              <p className="text-gray-600 dark:text-gray-400">Profile management features coming soon...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BarberDashboard;
